import React, { useState } from 'react';
import { Button, FormControl, Form, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { updateUserProfile } from '../../../../reducers/auth/auth.actions';
import { PROFILE } from '../../../../constants/routes';
import * as Yup from 'yup';
import { selectAuthUser } from '../../../../reducers/auth/auth.selectors';
import Loader from '../../../../components/Loader';
import { useHistory } from 'react-router-dom';
import { getLaravelValidationErrors } from '../../../../helpers';
import PasswordStrengthComponent from 'components/generic-components/PasswordStrengthComponent';
import { DeleteAccountModal } from './DeleteAccountModal';
import { CheckIcon } from '../../../../components/Icons';

const Account = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector(selectAuthUser);
  const history = useHistory();

  const [easy, setEasy] = useState(false);
  const [average, setAverage] = useState(false);
  const [strong, setStrong] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const isFromEmail = urlParams.get('reset');

  const { values, status, handleSubmit, errors, touched, handleChange, isSubmitting } = useFormik({
    initialValues: {
      email_notifications: user.email_notifications,
      push_notifications: user.push_notifications ? user.push_notifications : null,
      site_notifications: user.site_notifications ? user.site_notifications : null,
      current_password: '',
      password: '',
      password_confirmation: '',
      google: user.google ? user.google : '',
      resetPassword: isFromEmail,
    },
    validate: values => {
      values.password.length > 7 ? setEasy(true) : setEasy(false);
      values.password.length > 7 && /\d/.test(values.password) ? setAverage(true) : setAverage(false);
      values.password.length > 7 && /^(?=.*[A-Z])(?=.*\d).*$/.test(values.password)
        ? setStrong(true)
        : setStrong(false);
    },
    validationSchema: Yup.object().shape({
      password: Yup.string().trim().min(8),
      password_confirmation: Yup.string()
        .trim()
        .when('password', {
          is: password => !!password,
          then: Yup.string().required(),
        }),
      current_password: isFromEmail
        ? Yup.string().nullable()
        : Yup.string()
            .trim()
            .when('password', {
              is: password => !!password,
              then:user.metadata.can_set_password != true ? Yup.string().required()  : Yup.string(),
            }),
    }),
    onSubmit: (values, { setStatus, setSubmitting }) => {
      dispatch(
        updateUserProfile(user.id, {
          ...values,
          email_notifications: values.email_notifications ? 1 : 0,
          push_notifications: values.push_notifications ? 1 : 0,
          site_notifications: values.site_notifications ? 1 : 0,
        })
      )
        .then(resp => {
          history.push(PROFILE);
        })
        .catch(errors => {
          console.log('Form errors', errors);
          setStatus({
            errors: getLaravelValidationErrors(errors),
          });
          setSubmitting(false);
        });
    },
  });

  return (
    <div className="col px-md-5 py-3">
      <Loader isLoadin={isSubmitting} height={405} size={60}>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="my-3">
            <p className="acc-text-header">{t('security')}</p>
            <p className="acc-text-header acc-space">{t('email')}</p>
            <InputGroup size="lg">
              <FormControl
                type="text"
                className="border-bottom border-bottom-dark input-login font-size-12"
                disabled
                name="website"
                autoComplete="off"
                value={user.email}
                onChange={handleChange}
                isInvalid={touched.email && errors.email}
              />
            </InputGroup>
            {
              // <div className="acc-text-header">{t(!!user.email_verified_at ? 'Verified' : 'Unverified')}</div>
            }
          </Form.Group>

          {user && user.metadata.can_set_password != true && (
            <Form.Group className="my-3">
              <div className="acc-text-header">{t('Enter current password')}</div>
              <InputGroup size="lg">
                <FormControl
                  type="password"
                  className="border-bottom border-bottom-dark input-login input-password input-password-account font-size-12"
                  name="current_password"
                  autoComplete="off"
                  value={values.current_password}
                  onChange={handleChange}
                  placeholder={t('')}
                  isInvalid={touched.current_password && errors.current_password}
                />
                <Form.Control.Feedback type="invalid" tooltip>
                  {!!errors.current_password && errors.current_password}
                </Form.Control.Feedback>
              </InputGroup>
            </Form.Group>
          )}

          <Form.Group className="my-3">
            <div className="acc-text-header">{t('New password')}</div>
            <InputGroup size="lg">
              <FormControl
                type="password"
                className="border-bottom border-bottom-dark input-login input-password input-password-account font-size-12"
                name="password"
                autoComplete="off"
                value={values.password}
                onChange={handleChange}
                placeholder={t('')}
                isInvalid={touched.password && errors.password}
              />
              <Form.Control.Feedback type="invalid" tooltip>
                {!!errors.password && errors.password}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>

          <Form.Group className="my-3">
            <div className="acc-text-header">{t('Confirm new password')}</div>
            <InputGroup size="lg">
              <FormControl
                type="password"
                className="border-bottom border-bottom-dark input-login input-password input-password-account font-size-12"
                name="password_confirmation"
                autoComplete="off"
                value={values.password_confirmation}
                onChange={handleChange}
                placeholder={t('')}
                isInvalid={touched.password_confirmation && errors.password_confirmation}
              />
              <Form.Control.Feedback type="invalid" tooltip>
                {!!errors.password_confirmation && errors.password_confirmation}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>

          <PasswordStrengthComponent
            easy={easy}
            average={average}
            strong={strong}
            passwordError={!!errors.password_confirmation}
          />
          <div>
            <span className="acc-text-header"> {t('Please use at least 8 characters and complete 3 of this')}</span>
            <div />
            <div className="d-flex align-items-center">
              <CheckIcon height="1rem" width="1rem" fill="#8b95b1" />
              <span className="acc-text-header">{t('Lower case')}</span>
            </div>
            <div />
            <div className="d-flex align-items-center">
              <CheckIcon height="1rem" width="1rem" fill="#8b95b1" />
              <span className="acc-text-header">{t('Use upper case characters')}</span>
            </div>
            <div />
            <div className="d-flex align-items-center">
              <CheckIcon height="1rem" width="1rem" fill="#8b95b1" />
              <span className="acc-text-header">{t('Numerals')}</span>
            </div>
          </div>
          <Form.Group className="my-3">
            <p className="acc-text-header">{t('Linked Accounts')}</p>
            <p className="acc-text-header">{t('Google')}</p>
            <InputGroup size="lg">
              <FormControl
                type="text"
                className="border-bottom border-bottom-dark font-size-12"
                name="google"
                autoComplete="off"
                value={values.google}
                onChange={handleChange}
                placeholder={t('google.com/username')}
                isInvalid={touched.google && errors.google}
              />
              <Form.Control.Feedback type="invalid" tooltip>
                {!!errors.google && errors.google}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>

          <div className="acc-text-header acc-space">{t('notifications')}</div>
          <div className="d-flex my-2 justify-content-between">
            <div className="mr-4">
              <p className="acc-notification">{t('Email Notifications')}</p>
            </div>
            <div>
              <Form.Check
                type="switch"
                className="custom-checkmark-blue"
                label=""
                checked={values.email_notifications}
                name="email_notifications"
                onChange={handleChange}
                id="emailCheck"
              />
            </div>
          </div>
          <div className="d-flex my-2 justify-content-between">
            <div className="mr-4">
              <p className="acc-notification">{t('Push Notifications')}</p>
            </div>
            <div>
              <Form.Check
                type="switch"
                className="custom-checkmark-blue"
                label=""
                checked={values.push_notifications}
                name="push_notifications"
                onChange={handleChange}
                id="pushCheck"
              />
            </div>
          </div>
          <div className="d-flex my-2 justify-content-between">
            <div className="mr-4">
              <p className="acc-notification">{t('Site Notifications')}</p>
            </div>
            <div>
              <Form.Check
                type="switch"
                className="custom-checkmark-blue"
                label=""
                checked={values.site_notifications}
                name="site_notifications"
                onChange={handleChange}
                id="siteCheck"
              />
            </div>
          </div>
          {status &&
            status.errors &&
            status.errors.map((err, key) => (
              <p key={key} className="text-danger my-1">
                {err}
              </p>
            ))}
          <div className="d-flex justify-content-center  mt-4">
            <DeleteAccountModal>
              <Button type="button" className="d-flex acc-button mb-3 mr-2">
                {t('Delete Account')}
              </Button>
            </DeleteAccountModal>

            <Button type="submit" className="d-flex acc-button mb-3 mr-2">
              {t('Save')}
            </Button>
          </div>
        </Form>
      </Loader>
    </div>
  );
};

export default Account;
